/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { Logger, MessageService } from '@core/lib/services';
import { ProxyDataService } from '@app/core/http';
import { ClinicalCacheFactory } from '@app/ui/client-cache/factory/clinical-cache-factory';
import { SseClientCacheFactory } from '@app/ui/client-cache/factory/sse-client-cache-factory';
import { SseSearchClientFactory } from '@app/ui/client-cache/factory/sse-search-client-factory';

const log = new Logger('CacheFactoryService');

@Injectable({
  providedIn: 'root'
})
export class CacheFactoryService {
  private clinicalCacheFactory: Map<string, ClinicalCacheFactory> = new Map<string, ClinicalCacheFactory>();
  private sseClientCacheFactory: Map<string, SseClientCacheFactory> = new Map<string, SseClientCacheFactory>();
  private sseSearchClientCacheFactory: Map<string, SseSearchClientFactory> = new Map<string, SseSearchClientFactory>();

  constructor(private messageService: MessageService, private dataService: ProxyDataService) {}

  createClinicalCacheFactory(name, endpointFunction, comparator): ClinicalCacheFactory {
    const cacheFactory = new ClinicalCacheFactory(
      name,
      endpointFunction,
      comparator,
      log,
      this.messageService,
      this.dataService
    );

    this.clinicalCacheFactory.set(name, cacheFactory);
    return this.clinicalCacheFactory.get(name);
  }

  createSSEClientCacheFactory(name, endPointFunction, comparator): SseClientCacheFactory {
    const cacheFactory = new SseClientCacheFactory(
      name,
      endPointFunction,
      comparator,
      log,
      this.dataService,
      this.messageService
    );

    this.sseClientCacheFactory.set(name, cacheFactory);
    return this.sseClientCacheFactory.get(name);
  }

  createSSESearchClientCacheFactory(name): SseSearchClientFactory {
    const cacheFactory = new SseSearchClientFactory();

    this.sseSearchClientCacheFactory.set(name, cacheFactory);
    return this.sseSearchClientCacheFactory.get(name);
  }

  idMappingAddHandler(idMap: any, data, patientId, requestedProcedureId) {
    if (!idMap[patientId]) {
      idMap[patientId] = [requestedProcedureId];
    } else if (!_.contains(idMap[patientId], requestedProcedureId)) {
      idMap[patientId].push(requestedProcedureId);
    }
    return data;
  }

  idMappingRemoveHandler(idMap: any, requestedProcedureId) {
    let finalPatientId;
    const patientIds = _.keys(idMap);

    for (let i = 0; i < patientIds.length; i++) {
      const patientId = patientIds[i];
      const rpIdArray = idMap[patientId];
      const rpIdIndex = rpIdArray.indexOf(requestedProcedureId);
      if (rpIdIndex < 0) {
        continue;
      }

      finalPatientId = parseInt(patientId);
      if (rpIdArray.length === 1) {
        delete idMap[patientId];
      } else {
        rpIdArray.splice(rpIdIndex, 1);
        return undefined;
      }
      break;
    }

    return finalPatientId;
  }
}
